import {
    Button,
    TextField,
    Typography,
    Modal,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox,
    Icon,
    SelectChangeEvent,
} from '@mui/material'
import {
    addOrg,
    fetchAllApplicationsList,
    updateOrg,
} from '../../services/orgServices'
import { fetchUserOrganizations } from '../../services/userServices'
import {
    Application,
    FlattenedApplication,
    IAddOrg,
    IDefaultEntityData,
    IOrgformData,
    ReportTypeId,
} from './interfaces'
import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
    ChangeEvent,
} from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import TruncatedText from '../Common/TruncatedText'
import RealTimeAlertEventsCheckbox from '../ViewEntity/Components/RealTimeAlertEventsCheckbox'
import { ORG_ADD_MODE, ROOT_ORG } from './constants'
import CheckboxInput from '../Common/CheckboxInput'
import { CloseOutlined } from '@mui/icons-material'
import { useUnits } from '../../hooks/useUnits'
import UnitsPreferences from '../Layout/Navbar/UnitsPreferences'
import {
    Applications,
    defaultFormData,
    ReportTypes,
} from '../../constants/constants'
import { IOrganization } from '../Layout/Navbar/interfaces'
import { updateOrg as updateOrgInStore } from '../../redux/slices/orgSlice'
import theme from '../../theme/theme'
import LocalizationPreferences from '../Layout/Navbar/LocalizationPreferences'
import {
    defaultLocalization,
    ISO_DATE_TIME_FORMAT,
} from '../../utils/DateAndTimeUtils'
import { useLocalization } from '../../hooks/useLocalization'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import DateTimeInputISO from '../Common/DateTimeInputISO'
import GenericDropdown, {
    IGenericDropdownOptions,
} from '../Applications/Intransit/GenericDropdown'
import PaperModalComponent from '../Common/PaperModalComponent'
import InsurancePreferences from './Preferences/InsurancePreferences'
import ProductPreferences from './Preferences/ProductPreferences'
import LocationPreferences from './Preferences/LocationPreferences'

export default function AddOrg(props: Readonly<IAddOrg>): ReactElement {
    const { t } = useTranslation()
    const { openAddOrEditOrg, handleAddOrEditOrg, readOnly } = props
    const dispatch = useAppDispatch()

    const unitsList = useAppSelector((state) => state.user.unitsList)
    const { open, mode, formData: data } = openAddOrEditOrg

    const org = useAppSelector((state) => state.org.org)

    const [onlyEnterpriseOrgs, setOnlyEnterpriseOrgs] = useState<
        IOrganization[]
    >([])

    useEffect(() => {
        fetchUserOrganizations({ size: 10000, onlyEnterprises: true }).then(
            (res) => {
                if (res?.organizations) {
                    setOnlyEnterpriseOrgs(
                        res.organizations.filter(
                            (i) => i.name.toLowerCase() !== ROOT_ORG,
                        ),
                    )
                }
            },
        )
    }, [])

    const initialFormValues = useMemo<IOrgformData>(
        () => ({
            ...data,
            parentOrganizationId:
                mode === ORG_ADD_MODE.ADD
                    ? onlyEnterpriseOrgs[0]?.id
                    : data.parentOrganizationId,
            preferences: {
                ...data.preferences,
                syncStartDate: data.preferences.syncStartDate
                    ? dayjs(data.preferences.syncStartDate).format(
                          ISO_DATE_TIME_FORMAT,
                      )
                    : dayjs().format(ISO_DATE_TIME_FORMAT),
            },
        }),
        [data, mode, onlyEnterpriseOrgs],
    )
    const [formValues, setFormValues] = useState<IOrgformData>(
        () => initialFormValues,
    )
    const [errors, setErrors] = useState<{
        parentOrganizationId?: string
        name?: string
        description?: string
        token?: string
        systemPolicyNumber?: string
        primaryAssuredCompanyCode?: string
    }>({}) // Provide optional types for error messages
    const [applicationsList, setApplicationsList] = useState<
        FlattenedApplication[]
    >([])
    const initialUnits = useMemo(() => {
        if (unitsList?.length) {
            const orgUnits = data?.orgUnits
            return unitsList?.map((i) => ({
                ...i,
                selected: orgUnits?.[i?.id] ?? i?.units?.[0],
            }))
        }
        return []
    }, [unitsList, data?.orgUnits])
    const { units, onChangeUnits, isUnitsUpdated } = useUnits(initialUnits)

    // set initial localization values
    const initialValues = useMemo(() => {
        // For ADD_ENTERPRICE, always use defaultLocalization
        if (mode === ORG_ADD_MODE.ADD_ENTERPRICE) {
            return defaultLocalization
        }

        // For EDIT, ADD, EDIT_ENTERPRICE: use org's localization if available, else default
        if (
            (mode === ORG_ADD_MODE.EDIT ||
                mode === ORG_ADD_MODE.ADD ||
                mode === ORG_ADD_MODE.EDIT_ENTERPRICE) &&
            formValues?.localization
        ) {
            return formValues.localization
        }

        return defaultLocalization
    }, [mode, formValues?.localization])
    // hook to handle localization change
    const { localization, handleLocalizationChange, isLocalizationUpdated } =
        useLocalization(initialValues)

    const isSyncEnabled = useMemo(
        () =>
            formValues.preferences.isSync !== 'false' &&
            formValues.preferences.isSync,
        [formValues.preferences.isSync],
    )
    const isInsuranceSet = !!formValues.applicationAccess?.includes(
        Applications.cargoInsurance.id,
    )

    const isAddOrEditMode = useMemo(
        () => mode === ORG_ADD_MODE.ADD || mode === ORG_ADD_MODE.EDIT,
        [mode],
    )

    const isEditMode = useMemo(
        () =>
            mode === ORG_ADD_MODE.EDIT || mode === ORG_ADD_MODE.EDIT_ENTERPRICE,
        [mode],
    )

    const titleText = useMemo(() => {
        let title = ''

        if (isEditMode) {
            if (readOnly) {
                title = t('viewOrganization')
            } else {
                title = t('editOrganization')
            }
        } else {
            title = t('addOrganization')
        }
        return title
    }, [isEditMode, readOnly, t])

    const flattenApplications = useCallback(
        (
            items: Application[],
            parent: string,
            result: FlattenedApplication[] = [],
        ): FlattenedApplication[] => {
            for (const item of items) {
                result.push({ name: item.name, parent })
                if (item.subApplications) {
                    flattenApplications(item.subApplications, item.name, result)
                }
            }
            return result
        },
        [],
    )

    const getApplications = useCallback(async () => {
        const applications = await fetchAllApplicationsList()
        const flattenedApps = flattenApplications(
            applications?.Applications || [],
            '',
        )
        setApplicationsList(flattenedApps)
    }, [flattenApplications])

    useEffect(() => {
        getApplications()
    }, [getApplications])

    const isFormUnchanged = useMemo(() => {
        return (
            formValues.name === data.name &&
            formValues.description === data.description &&
            formValues.parentOrganizationId === data.parentOrganizationId &&
            formValues.preferences.token === data.preferences.token &&
            formValues.preferences.isSync === data.preferences.isSync &&
            formValues.preferences.syncStartDate ===
                data.preferences.syncStartDate &&
            formValues.preferences.handGrenade ===
                data.preferences.handGrenade &&
            formValues.preferences.deviceAutoPowerOff ===
                data.preferences.deviceAutoPowerOff &&
            formValues.preferences.excludeTriggeredEvents ===
                data.preferences.excludeTriggeredEvents &&
            formValues.preferences.fixTemperatureLocationDrift ===
                data.preferences.fixTemperatureLocationDrift &&
            formValues.applicationAccess === data.applicationAccess &&
            formValues.accountId === data.accountId &&
            isEditMode
        )
    }, [formValues, data, isEditMode])

    const isUpdated = !isFormUnchanged

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target
            setFormValues((prev) => ({
                ...prev,
                [name]: value,
            }))

            // Clear errors when user starts typing
            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }))
        },
        [],
    )

    const onChangeOrg = useCallback((orgValue: string) => {
        setFormValues((prev) => ({
            ...prev,
            parentOrganizationId: orgValue,
        }))

        // Clear errors when user starts typing
        setErrors((prev) => ({
            ...prev,
            parentOrganizationId: '',
        }))
    }, [])

    const onChangeReportType = useCallback(
        (event: SelectChangeEvent<string>) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    reportType: event.target.value as ReportTypeId,
                },
            }))
        },
        [],
    )

    const onChangeToken = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    token: e.target.value,
                },
            }))

            // Clear errors when user starts typing
            setErrors((prev) => ({
                ...prev,
                token: '',
            }))
        },
        [],
    )

    const handleSyncChange = useCallback(
        (id: string, val: boolean) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    isSync: val,
                    token: '',
                    /**
                     * If Sync is enabled, disable Hand Grenade by default
                     */
                    handGrenade: val ? false : data.preferences.handGrenade,
                },
            }))
        },
        [data.preferences.handGrenade],
    )

    const handleEnableHandGrenadeChange = useCallback(
        (id: string, val: boolean) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    handGrenade: val,
                    /**
                     * If Hand Grenade is enabled, disable sync by default
                     */
                    isSync: val ? false : data.preferences.isSync,
                },
            }))
        },
        [data.preferences.isSync],
    )

    const handleDeviceAutoPowerOffChange = useCallback(
        (id: string, val: boolean) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    deviceAutoPowerOff: val,
                },
            }))
        },
        [],
    )

    const handleFixLocationDriftChange = useCallback(
        (id: string, val: boolean) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    fixTemperatureLocationDrift: val,
                },
            }))
        },
        [],
    )

    const handleIncludeRealTimeAlertEventsChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setFormValues((prev) => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    excludeTriggeredEvents: (!e.target.checked).toString(),
                },
            }))
        },
        [],
    )

    const handleChangeSyncDateTime = useCallback((id: string, date: string) => {
        setFormValues((prev) => ({
            ...prev,
            preferences: {
                ...prev.preferences,
                syncStartDate: date || dayjs().format(ISO_DATE_TIME_FORMAT),
            },
        }))
    }, [])

    const handleInsurancePreferenceChange = useCallback(
        (id: string, value: unknown) => {
            setFormValues((prev) => ({
                ...prev,
                insuranceDetails: [
                    {
                        ...prev.insuranceDetails[0],
                        [id]: value,
                    },
                ],
            }))
            setErrors((prev) => ({
                ...prev,
                [id]: '',
            }))
        },
        [],
    )

    const handleProductPreferenceChange = useCallback(
        (value: IDefaultEntityData | null) => {
            setFormValues((prev) => ({
                ...prev,
                defaultProduct: value,
            }))
        },
        [],
    )

    const handleLocationPreferenceChange = useCallback(
        (
            field: 'defaultShipper' | 'defaultReceiver',
            value: IDefaultEntityData | null,
        ) => {
            setFormValues((prev) => ({
                ...prev,
                [field]: value,
            }))
        },
        [],
    )
    const validateForm = useCallback(() => {
        const { name, description, parentOrganizationId } = formValues
        const nextErrors: {
            name?: string
            description?: string
            parentOrganizationId?: string
            token?: string
            systemPolicyNumber?: string
            primaryAssuredCompanyCode?: string
        } = {}

        if (!name?.trim()) {
            nextErrors.name = t('nameRequired')
        }

        if (!description?.trim()) {
            nextErrors.description = t('descriptionRequired')
        }

        if (!parentOrganizationId?.trim() && isAddOrEditMode) {
            nextErrors.parentOrganizationId = t('organizationRequired')
        }

        if (isInsuranceSet) {
            const insurance = formValues.insuranceDetails?.[0]
            if (!insurance?.systemPolicyNumber?.trim()) {
                nextErrors.systemPolicyNumber = t('policyNumberRequired')
            }
            if (
                !insurance?.primaryAssuredCompanyCode ||
                insurance.primaryAssuredCompanyCode.length === 0
            ) {
                nextErrors.primaryAssuredCompanyCode = t(
                    'primaryAssuredCompanyCodeRequired',
                )
            }
        }

        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }, [formValues, isAddOrEditMode, isInsuranceSet, t])

    const clearForm = useCallback(() => {
        setFormValues({ ...defaultFormData })
        setErrors({})
    }, [])

    const prepareFormData = useCallback(() => {
        const updatedUints: { [id: string]: string } = {}
        if (units) {
            for (const i of units) {
                updatedUints[i.id] = i.selected || ''
            }
        }

        const updateStartDate = formValues?.preferences?.syncStartDate
            ? dayjs(formValues?.preferences?.syncStartDate)
                  .utc()
                  .format(ISO_DATE_TIME_FORMAT)
            : ''

        return {
            ...formValues,
            orgUnits: updatedUints,
            localization,
            preferences: {
                ...formValues.preferences,
                syncStartDate: updateStartDate,
            },
        }
    }, [formValues, localization, units])

    const createUpdatedOrg = useCallback(
        (preparedData: IOrgformData, updateStartDate: string) => {
            const savedOrg = JSON.parse(
                localStorage.getItem('DEFAULT_ORG') || '{}',
            ) as IOrganization

            return {
                ...savedOrg,
                id: preparedData?.id ?? org?.id ?? '',
                name: preparedData?.name ?? org?.name,
                description: preparedData?.description ?? org?.description,
                preferences: {
                    ...org?.preferences,
                    data: {
                        ...org?.preferences?.data,
                        isSync: preparedData?.preferences?.isSync ?? 'false',
                        token: preparedData?.preferences?.token ?? '',
                        syncStartDate: updateStartDate,
                        handGrenade:
                            preparedData?.preferences?.handGrenade ?? false,
                        deviceAutoPowerOff:
                            preparedData?.preferences?.deviceAutoPowerOff ??
                            false,
                        excludeTriggeredEvents:
                            preparedData?.preferences?.excludeTriggeredEvents ??
                            false,
                        fixTemperatureLocationDrift:
                            preparedData?.preferences
                                ?.fixTemperatureLocationDrift ?? false,
                    },
                    localization,
                    insuranceDetails: preparedData?.insuranceDetails,
                    defaultProduct: preparedData?.defaultProduct,
                    defaultShipper: preparedData?.defaultShipper,
                    defaultReceiver: preparedData?.defaultReceiver,
                },
                applicationAccess:
                    preparedData?.applicationAccess ?? org?.applicationAccess,
                orgUnits: {
                    ...savedOrg?.orgUnits,
                    units: preparedData.orgUnits,
                },
                localization,
                accountId: preparedData?.accountId ?? org?.accountId ?? '',
            } as IOrganization
        },
        [localization, org],
    )

    const handleEditOrg = useCallback(
        async (preparedData: IOrgformData) => {
            const updatedOrg = createUpdatedOrg(
                preparedData,
                preparedData.preferences.syncStartDate,
            )
            await updateOrg(preparedData, org?.id || '')
            const isCurrentLoggedInOrg = preparedData.id === org?.id
            if (isCurrentLoggedInOrg) {
                localStorage.setItem('DEFAULT_ORG', JSON.stringify(updatedOrg))
                dispatch(updateOrgInStore(updatedOrg))
            }
        },
        [createUpdatedOrg, dispatch, org?.id],
    )

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (!validateForm()) {
                return
            }

            const preparedData = prepareFormData()

            if (isEditMode) {
                await handleEditOrg(preparedData)
            } else {
                await addOrg(preparedData, org?.id || '')
            }

            clearForm()
            handleAddOrEditOrg(true)
        },
        [
            clearForm,
            handleAddOrEditOrg,
            handleEditOrg,
            isEditMode,
            org?.id,
            prepareFormData,
            validateForm,
        ],
    )

    const handleChangeApplication = useCallback(
        (name: string) => {
            setFormValues((prev) => {
                let updatedApplicationAccess = prev.applicationAccess?.length
                    ? [...prev.applicationAccess]
                    : []

                if (updatedApplicationAccess.includes(name)) {
                    const subApplications = new Set(
                        applicationsList
                            .filter((i) => i.parent === name)
                            .map((i) => i.name),
                    )
                    updatedApplicationAccess = updatedApplicationAccess.filter(
                        (i) => i !== name && !subApplications.has(i),
                    )
                } else {
                    updatedApplicationAccess = [
                        ...updatedApplicationAccess,
                        name,
                    ]
                }

                return {
                    ...prev,
                    applicationAccess: updatedApplicationAccess,
                }
            })
        },
        [applicationsList],
    )

    const selectedApplicationsText = useMemo(
        () => (formValues.applicationAccess || []).filter(Boolean).join(', '),
        [formValues.applicationAccess],
    )

    const renderSelectedApplications = useCallback(
        (selected: string[]) => {
            if (selected.length === 0) {
                return ''
            }

            return (
                <Box
                    width="100%"
                    id="applications-select-value"
                    data-testid="applications-select-value"
                >
                    <TruncatedText
                        sx={{
                            fontSize: '14px',
                        }}
                        text={selectedApplicationsText}
                    />
                </Box>
            )
        },
        [selectedApplicationsText],
    )

    const minSyncDateTime = useMemo(
        () => dayjs().subtract(90, 'day').toISOString(),
        [],
    )
    const maxSyncDateTime = useMemo(
        () => dayjs().format(ISO_DATE_TIME_FORMAT),
        [],
    )
    return (
        <div>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <PaperModalComponent>
                    <form onSubmit={handleSubmit} id="org-form">
                        <Box
                            sx={{
                                p: 4,
                                boxShadow: 4,
                                borderRadius: 4,
                                overflowY: 'auto',
                                position: 'absolute' as const,
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '80%',
                                maxWidth: '1200px',
                                backgroundColor: '#fff',
                                border: `2px solid ${theme.palette.primary.main}`,
                                maxHeight: '90vh',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '15px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon
                                    id="close-icon"
                                    onClick={() => handleAddOrEditOrg(false)}
                                    sx={{ marginRight: '8px' }}
                                    fontSize="small"
                                    aria-label={t('close')}
                                    title={t('close')}
                                >
                                    <CloseOutlined
                                        sx={{
                                            color: '#000',
                                            marginTop: '-2px',
                                        }}
                                    />
                                </Icon>
                            </Box>
                            <Typography
                                variant="h6"
                                gutterBottom
                                component="h2"
                                fontSize={18}
                                // id="modal-modal-title"
                                id="draggable-modal-title"
                                sx={{ cursor: 'move' }}
                            >
                                {titleText}
                            </Typography>
                            <Box
                                sx={{ mt: 2, mb: 4 }}
                                id="modal-modal-description"
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 2, fontSize: '18px' }}
                                >
                                    {t('organizationDetails')}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '20px',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    {isAddOrEditMode ? (
                                        <FormControl fullWidth>
                                            <InputLabel id="Organizations-select-label">
                                                {t('enterpriseOrganization')}
                                            </InputLabel>
                                            <Select
                                                labelId="Organizations-select-label"
                                                id="Organizations-select"
                                                value={
                                                    formValues.parentOrganizationId
                                                }
                                                label={`${t('enterpriseOrganizationText')} *-----`}
                                                onChange={(e) =>
                                                    onChangeOrg(e.target.value)
                                                }
                                                disabled={
                                                    mode ===
                                                        ORG_ADD_MODE.EDIT ||
                                                    readOnly
                                                }
                                                sx={{ fontSize: '14px' }}
                                                MenuProps={{
                                                    anchorOrigin: {
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    },
                                                    transformOrigin: {
                                                        vertical: 'top',
                                                        horizontal: 'left',
                                                    },
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 280,
                                                        },
                                                        sx: {
                                                            width: 'auto',
                                                            minWidth: '100%',
                                                        },
                                                    },
                                                }}
                                                error={
                                                    !!errors.parentOrganizationId
                                                }
                                            >
                                                {onlyEnterpriseOrgs?.map(
                                                    (o) => (
                                                        <MenuItem
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                            }}
                                                            key={o.id}
                                                            id={o.id}
                                                            value={o.id}
                                                        >
                                                            <TruncatedText
                                                                text={o.name}
                                                                sx={{
                                                                    fontSize:
                                                                        '16px',
                                                                }}
                                                            />
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                            {errors.parentOrganizationId && (
                                                <FormHelperText
                                                    sx={{ color: 'red' }}
                                                >
                                                    {
                                                        errors.parentOrganizationId
                                                    }
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    ) : null}
                                    <TextField
                                        id="add-org-name"
                                        name="name"
                                        label={t('nameMust')}
                                        variant="outlined"
                                        type="text"
                                        value={formValues.name}
                                        onChange={handleInputChange}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        fullWidth
                                        disabled={readOnly}
                                    />
                                    <TextField
                                        id="add-org-description"
                                        name="description"
                                        label={t('descriptionMust')}
                                        type="text"
                                        value={formValues.description}
                                        onChange={handleInputChange}
                                        error={!!errors.description}
                                        helperText={errors.description}
                                        fullWidth
                                        disabled={readOnly}
                                    />
                                    <FormControl fullWidth disabled={readOnly}>
                                        <InputLabel id="Organizations-applications">
                                            {t('applications')}
                                        </InputLabel>
                                        <Select
                                            labelId="Organizations-applications"
                                            id="Organizations-applications-select"
                                            value={
                                                formValues?.applicationAccess ||
                                                []
                                            }
                                            label={t('applications')}
                                            multiple
                                            sx={{ fontSize: '14px' }}
                                            renderValue={
                                                renderSelectedApplications
                                            }
                                            MenuProps={{
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 280,
                                                    },
                                                    sx: {
                                                        width: 'auto',
                                                        minWidth: '100%',
                                                    },
                                                },
                                            }}
                                        >
                                            {applicationsList?.map((o) => (
                                                <MenuItem
                                                    sx={{
                                                        fontSize: '14px',
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        padding:
                                                            '5px 15px 5px 10px',
                                                        paddingLeft: `${
                                                            o.parent
                                                                ? '38px'
                                                                : ''
                                                        }`,
                                                    }}
                                                    key={o.name}
                                                    id={o.name}
                                                    value={o.name}
                                                    disabled={
                                                        o.parent
                                                            ? !formValues?.applicationAccess?.includes(
                                                                  o.parent,
                                                              )
                                                            : false
                                                    }
                                                    onClick={() =>
                                                        handleChangeApplication(
                                                            o.name,
                                                        )
                                                    }
                                                >
                                                    <Checkbox
                                                        id={`${o.name}-checkbox`}
                                                        size="small"
                                                        checked={formValues?.applicationAccess?.includes(
                                                            o.name,
                                                        )}
                                                    />
                                                    <TruncatedText
                                                        text={o.name}
                                                        sx={{
                                                            fontSize: '16px',
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <GenericDropdown
                                        isLoading={false}
                                        id="add-org-reportType"
                                        name="reportType"
                                        label={t('CCLReportType')}
                                        hideLabel={true}
                                        value={
                                            formValues.preferences.reportType
                                        }
                                        onChange={onChangeReportType}
                                        options={
                                            ReportTypes as unknown as IGenericDropdownOptions[]
                                        }
                                        selectElementWidth="100%"
                                        marginBottom="0px"
                                        disabled={readOnly}
                                    />
                                    <RealTimeAlertEventsCheckbox
                                        checked={
                                            formValues.preferences
                                                .excludeTriggeredEvents !==
                                            'true'
                                        }
                                        onChange={
                                            handleIncludeRealTimeAlertEventsChange
                                        }
                                        sx={{ mr: 0 }}
                                        disabled={readOnly}
                                    />
                                    <CheckboxInput
                                        id="add-org-fixTemperatureLocationDrift"
                                        label={t('fixTemperatureLocationDrift')}
                                        value={
                                            formValues.preferences
                                                .fixTemperatureLocationDrift
                                        }
                                        onChange={handleFixLocationDriftChange}
                                        disabled={readOnly}
                                    />
                                    <CheckboxInput
                                        id="add-org-deviceAutoPowerOff"
                                        label={t('deviceAutoPowerOff')}
                                        value={
                                            formValues.preferences
                                                .deviceAutoPowerOff
                                        }
                                        onChange={
                                            handleDeviceAutoPowerOffChange
                                        }
                                        disabled={readOnly}
                                    />
                                    <CheckboxInput
                                        id="add-org-enableHandGrenade"
                                        label={t('enableHandGrenade')}
                                        value={
                                            formValues.preferences.handGrenade
                                        }
                                        onChange={handleEnableHandGrenadeChange}
                                        helperText={t(
                                            'enableHandGrenadeHelperText',
                                        )}
                                        disabled={readOnly}
                                    />
                                    {isAddOrEditMode ? (
                                        <>
                                            <CheckboxInput
                                                id="add-org-sync"
                                                label="Enable sync"
                                                value={isSyncEnabled}
                                                onChange={handleSyncChange}
                                                disabled={readOnly}
                                            />
                                            <TextField
                                                id="add-org-token"
                                                name="token"
                                                label="Token"
                                                type="text"
                                                disabled={
                                                    !isSyncEnabled || readOnly
                                                }
                                                value={
                                                    formValues.preferences.token
                                                }
                                                onChange={onChangeToken}
                                                error={!!errors.token}
                                                helperText={errors.token}
                                                fullWidth
                                            />
                                            <TextField
                                                id="add-org-accountId"
                                                name="accountId"
                                                label="Flashtrak Account Id"
                                                type="text"
                                                value={formValues.accountId}
                                                onChange={handleInputChange}
                                                fullWidth
                                                disabled={readOnly}
                                            />
                                            <DateTimeInputISO
                                                id="add-org-syncStartDate"
                                                label="Sync Start Date"
                                                isLegend={true}
                                                value={
                                                    formValues.preferences
                                                        ?.syncStartDate
                                                }
                                                disabled={
                                                    !isSyncEnabled || readOnly
                                                }
                                                onChange={
                                                    handleChangeSyncDateTime
                                                }
                                                errorMessage={''}
                                                size="medium"
                                                minDateTime={minSyncDateTime}
                                                maxDateTime={maxSyncDateTime}
                                            />
                                        </>
                                    ) : null}
                                </Box>
                            </Box>
                            <Box sx={{ mb: 4 }}>
                                <LocalizationPreferences
                                    initialPreferences={localization}
                                    handleLocalizationChange={
                                        handleLocalizationChange
                                    }
                                    disabled={readOnly}
                                />
                            </Box>
                            <Box sx={{ mb: 4 }}>
                                <InsurancePreferences
                                    InsurancePreferences={
                                        formValues.insuranceDetails[0]
                                    }
                                    handleInsurancePreferenceChange={
                                        handleInsurancePreferenceChange
                                    }
                                    isInsuranceSet={isInsuranceSet}
                                    errors={errors}
                                    disabled={readOnly}
                                />
                            </Box>
                            <Box sx={{ mb: 4 }}>
                                <ProductPreferences
                                    orgId={formValues?.id || ''}
                                    productPreferences={
                                        formValues.defaultProduct
                                    }
                                    handleProductPreferenceChange={
                                        handleProductPreferenceChange
                                    }
                                    disabled={readOnly}
                                />
                            </Box>
                            <Box sx={{ mb: 4 }}>
                                <LocationPreferences
                                    orgId={formValues?.id || ''}
                                    defaultShipper={formValues.defaultShipper}
                                    defaultReceiver={formValues.defaultReceiver}
                                    handleLocationPreferenceChange={
                                        handleLocationPreferenceChange
                                    }
                                    disabled={readOnly}
                                />
                            </Box>
                            <UnitsPreferences
                                units={units}
                                onChangeUnits={onChangeUnits}
                                disabled={readOnly}
                            />
                            <Button
                                sx={{
                                    mt: 4,
                                    maxWidth: '200px',
                                    alignSelf: 'center',
                                }}
                                variant="contained"
                                color="primary"
                                id="add-org-submit-button"
                                disabled={
                                    readOnly ||
                                    (!isUpdated &&
                                        !isUnitsUpdated &&
                                        !isLocalizationUpdated)
                                }
                                type="submit"
                            >
                                {t('submit')}
                            </Button>
                        </Box>
                    </form>
                </PaperModalComponent>
            </Modal>
        </div>
    )
}
